"use client";

import { useCallback, useSyncExternalStore } from "react";

/**
 * Subscribes to a media query and returns whether it currently matches.
 *
 * `useSyncExternalStore` rather than state-in-an-effect: matchMedia is an
 * external store, and this way the first client render already reads the real
 * value instead of rendering `false` and then re-rendering.
 *
 * The server snapshot is always `false`, so anything gated on this hook must
 * be the *enhanced* branch — the fallback is what gets server-rendered.
 * Section 3's card stack uses it that way: the plain stacked list is the
 * baseline, and the pinned scroll animation switches on once we know the
 * viewport is big enough to hold a whole card.
 */
export function useMediaQuery(query: string) {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const mql = window.matchMedia(query);
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    },
    [query],
  );

  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false,
  );
}
